import { useNavigate, useParams } from 'react-router-dom'
import { ExternalLink, GitFork, GitPullRequest, Minimize2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { TypeGlyph } from '@/components/common/badges'
import {
  BodyEditor,
  Comments,
  FieldStack,
  HeaderActions,
  InlineTitle,
  RefList,
} from '@/components/sheet/issue-sheet'
import { useIssueDetail, useIssues } from '@/lib/query'
import { githubIssueUrl } from '@/lib/issue-url'
import { getLastView } from '@/lib/persistence'
import { useUiState } from '@/hooks/use-ui-state'

/** /issues/:number — full-page issue view, and the sharable deep link.
 * Same building blocks as the Sheet, but with room for long content
 * (helpdesk-synced emails) and the fields in a side rail.
 * The route loader has already 404'd non-numeric params. */
export default function IssuePage() {
  const { number } = useParams()
  const parsed = Number(number)
  const navigate = useNavigate()
  const { setSheetIssue } = useUiState()
  const detailQuery = useIssueDetail(parsed)
  const universeIssue = useIssues().data?.find((i) => i.number === parsed)
  const issue = detailQuery.data?.issue ?? universeIssue

  if (!issue) {
    // Closed issues aren't in the open-only universe, so a failed detail
    // fetch (stale deep link, deleted issue) must say so, not skeleton forever.
    if (detailQuery.isError) {
      return (
        <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm">
          <p>Couldn't load issue #{parsed}.</p>
          <p className="text-faint-foreground text-xs">
            It may be closed or deleted.{' '}
            <a
              className="text-secondary hover:underline"
              href={githubIssueUrl(parsed)}
              target="_blank"
              rel="noreferrer"
            >
              Check on GitHub
            </a>
          </p>
        </div>
      )
    }
    return (
      <div className="mx-auto flex max-w-4xl flex-col gap-3 px-6 py-5">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="scroll-y h-full">
      <div className="mx-auto flex max-w-5xl items-start gap-8 px-6 py-5">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-mono text-xs">#{issue.number}</span>
            <TypeGlyph typeName={issue.typeName} />
            {issue.state === 'CLOSED' && (
              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                Closed
              </Badge>
            )}
            <span className="flex-1" />
            <Button
              size="icon-sm"
              variant="ghost"
              className="size-6"
              title="Collapse to drawer"
              onClick={() => {
                setSheetIssue(parsed)
                navigate(getLastView() ?? '/triage')
              }}
            >
              <Minimize2 className="size-3.5" />
            </Button>
            <HeaderActions
              issue={issue}
              onDeleted={() => navigate(getLastView() ?? '/triage', { replace: true })}
            />
            <a
              href={issue.url}
              target="_blank"
              rel="noreferrer"
              className="text-secondary inline-flex items-center gap-1 text-xs hover:underline"
            >
              Open in GitHub
              <ExternalLink className="size-3" />
            </a>
          </div>
          <InlineTitle issue={issue} />
          <BodyEditor
            issue={issue}
            body={detailQuery.data?.body ?? issue.body}
            bodyHTML={detailQuery.data?.bodyHTML}
          />
          <Separator />
          {detailQuery.isError && (
            <p className="text-destructive text-[11px]">
              Couldn't load comments, sub-issues and linked PRs — fields still work.
            </p>
          )}
          {detailQuery.data && (
            <>
              <RefList
                title="Sub-issues"
                icon={<GitFork className="size-3" />}
                refs={detailQuery.data.subIssues}
              />
              <RefList
                title="Linked PRs"
                icon={<GitPullRequest className="size-3" />}
                refs={detailQuery.data.linkedPrs}
              />
              <Comments detail={detailQuery.data} />
            </>
          )}
        </div>
        <aside className="border-border w-[300px] shrink-0 rounded-lg border p-3">
          <FieldStack issue={issue} />
        </aside>
      </div>
    </div>
  )
}
